import { useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { themeSolves, unlockedThemes } from '@familyhub/shared';
import { useSession } from '../session';
import { GearIcon } from '../components/GearIcon';
import { useHoldUpdates } from '../platform/update';
import { canSeeModule } from '../modules';
import { saveAccounts, useSchaak } from './actions';
import type { Mode, SessionSummary } from './common';
import { usePack } from './pack';
import { NotationDrillScreen, NotationHome, NotationLesson } from './Notation';
import { AccountsSheet, Overview, ThemeSheet } from './Overview';
import { Session, Summary } from './Session';

/**
 * The Schaak module — Jurren's own chess trainer (docs/SCHAAK.md). This is
 * the shell: which view is showing, the top bar, and the sheets. The overview
 * lives in Overview.tsx, a training session in Session.tsx, a mistake's review
 * in Review.tsx and the notation course in Notation.tsx.
 *
 * Stopping a session that did anything shows its summary first: a session
 * should end on what it did, and on what comes back tomorrow.
 */
export function SchaakScreen() {
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : null;
  const displayName = session.status === 'in' ? session.me?.user.display_name : undefined;
  const state = useSchaak(userId);
  const pack = usePack();
  const [mode, setMode] = useState<Mode | null>(null);
  const [themeSheet, setThemeSheet] = useState<string | null>(null);
  const [showAccounts, setShowAccounts] = useState(false);
  const run = useRef<SessionSummary | null>(null);

  const training = !!mode && mode.kind !== 'summary' && !mode.kind.startsWith('notation');
  useHoldUpdates(!!mode && mode.kind !== 'summary');

  if (session.status === 'in' && !canSeeModule('schaak', displayName)) return <Navigate to="/" replace />;

  const start = (m: Mode) => {
    run.current = state ? { attempts: [], ratingFrom: state.rating, ratingTo: state.rating } : null;
    setMode(m);
  };
  const stop = () => {
    const r = run.current;
    run.current = null;
    if (training && r && r.attempts.length > 0) setMode({ kind: 'summary', summary: r });
    else setMode(mode?.kind.startsWith('notation-') ? { kind: 'notation' } : null);
  };

  return (
    <div className="app">
      <div className="topbar">
        {mode && mode.kind !== 'summary' ? (
          <button className="btn btn-quiet back" onClick={stop}>
            {mode.kind.startsWith('notation') ? '‹ Terug' : '‹ Stop'}
          </button>
        ) : (
          <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">
            ‹ Hub
          </Link>
        )}
        <h1>Schaak</h1>
        {mode ? (
          <span />
        ) : (
          <button className="btn btn-quiet" aria-label="Accounts" onClick={() => setShowAccounts(true)}>
            <GearIcon />
          </button>
        )}
      </div>

      {!state || !pack || !userId ? null : mode?.kind === 'summary' ? (
        <Summary summary={mode.summary} state={state} onDone={() => setMode(null)} />
      ) : mode?.kind === 'notation' ? (
        <NotationHome
          state={state}
          onLesson={() => setMode({ kind: 'notation-lesson' })}
          onDrill={(drill) => setMode({ kind: 'notation-drill', drill })}
        />
      ) : mode?.kind === 'notation-lesson' ? (
        <NotationLesson onStart={() => setMode({ kind: 'notation-drill', drill: 'find' })} />
      ) : mode?.kind === 'notation-drill' ? (
        <NotationDrillScreen key={mode.drill} drill={mode.drill} state={state} pack={pack} userId={userId} />
      ) : mode ? (
        <Session
          key={JSON.stringify(mode)}
          mode={mode}
          state={state}
          pack={pack}
          userId={userId}
          onStop={stop}
          onAttempt={(label, solved, rating) => {
            if (!run.current) return;
            run.current.attempts.push({ label, solved });
            run.current.ratingTo = rating;
          }}
        />
      ) : (
        <Overview
          state={state}
          pack={pack}
          actions={{
            onTrain: (themes) => start({ kind: 'train', themes: themes ?? null }),
            onWoodpecker: () => start({ kind: 'woodpecker' }),
            onTheme: setThemeSheet,
            onMistakes: (gameId) => start({ kind: 'mistakes', gameId }),
            onAccounts: () => setShowAccounts(true),
            onNotation: () => setMode({ kind: 'notation' }),
          }}
        />
      )}

      {showAccounts && state && userId && (
        <AccountsSheet
          accounts={state.accounts}
          onSave={(l, c) => void saveAccounts(userId, l, c)}
          onClose={() => setShowAccounts(false)}
        />
      )}

      {themeSheet && state && (
        <ThemeSheet
          theme={themeSheet}
          solves={themeSolves(state.cards, themeSheet)}
          unlocked={unlockedThemes(state.cards).includes(themeSheet)}
          onStart={() => {
            start({ kind: 'train', themes: [themeSheet] });
            setThemeSheet(null);
          }}
          onClose={() => setThemeSheet(null)}
        />
      )}
    </div>
  );
}
